import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ListChecks, Plus, Search } from 'lucide-react';
import { EmptyState, ErrorState, PageHeader, Spinner } from '../components/common/States';
import ShortlistForm from '../components/shortlists/ShortlistForm';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { useShortlists } from '../hooks/queries';
import { api, errorMessage } from '../lib/api';
import { purposeLabel } from '../lib/taxonomy';
import { fmtDateTime, fmtEur, fmtInt } from '../lib/format';
import { cn } from '../lib/utils';

export default function Shortlists() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useShortlists();
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState('');
  const [status, setStatus] = useState('open');

  const create = useMutation({
    mutationFn: api.createShortlist,
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['shortlists'] });
      setCreating(false);
      navigate(`/shortlists/${s.id}`);
    },
  });

  const rows = useMemo(() => {
    const f = filter.toLowerCase();
    return (data || [])
      .filter((s) => status === 'all' || (s.status || 'open') === status)
      .filter((s) => !f || [s.name, s.client, s.owner_name, s.notes].some((x) => (x || '').toLowerCase().includes(f)));
  }, [data, filter, status]);
  const counts = useMemo(() => {
    const c = { open: 0, closed: 0, all: (data || []).length };
    (data || []).forEach((s) => (c[s.status || 'open'] = (c[s.status || 'open'] || 0) + 1));
    return c;
  }, [data]);

  return (
    <div className="w-full space-y-4 px-3 pb-16 pt-4 sm:px-6 lg:pt-6">
      <PageHeader
        title="Shortlists"
        description="B2B projects, bundles and ideas built from problem stock."
        actions={
          <Button size="sm" className="h-9 gap-1.5" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> New shortlist
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name, client, owner…" className="h-9 pl-8" />
        </div>
        <div className="inline-flex rounded-lg bg-muted p-1">
          {['open', 'closed', 'all'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={cn('rounded-md px-3 py-1 text-xs font-medium capitalize', status === s ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground')}
            >
              {s} <span className="tabular-nums text-muted-foreground">{counts[s] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>

      {error && <ErrorState error={error} onRetry={refetch} title="Could not load shortlists" />}
      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      )}
      {!isLoading && !error && !rows.length && (
        <EmptyState
          icon={ListChecks}
          title={data?.length ? 'No shortlist matches' : 'No shortlists yet'}
          action={
            !data?.length && (
              <Button size="sm" onClick={() => setCreating(true)}>
                <Plus className="mr-1.5 h-4 w-4" /> Create the first one
              </Button>
            )
          }
        >
          {data?.length ? 'Try another filter or status.' : 'Collect problem stock for a client project, a bundle or an idea — then export it or share the link.'}
        </EmptyState>
      )}

      {rows.length > 0 && (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Items</TableHead>
                <TableHead className="text-right">Units</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} className="cursor-pointer" onClick={() => navigate(`/shortlists/${s.id}`)}>
                  <TableCell className="max-w-[280px]">
                    <div className="truncate font-medium">{s.name}</div>
                    {s.notes && <div className="truncate text-xs text-muted-foreground">{s.notes}</div>}
                  </TableCell>
                  <TableCell>
                    <Badge variant="muted">{purposeLabel(s.purpose)}</Badge>
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate">{s.client || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell>
                    <Badge variant={s.status === 'closed' ? 'muted' : 'success'} className="capitalize">
                      {s.status || 'open'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmtInt(s.item_count)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtInt(s.total_units)}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{fmtEur(s.total_value)}</TableCell>
                  <TableCell className="whitespace-nowrap">{s.owner_name || '—'}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDateTime(s.updated_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={creating} onOpenChange={(o) => {
          setCreating(o);
          if (!o) create.reset();
        }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New shortlist</DialogTitle>
            <DialogDescription>Group problem stock for a project, a bundle or an idea.</DialogDescription>
          </DialogHeader>
          <ShortlistForm id="create-shortlist" onSubmit={(v) => create.mutate(v)} autoFocus />
          {create.error && <p className="text-sm text-destructive">{errorMessage(create.error)}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button type="submit" form="create-shortlist" disabled={create.isPending}>
              {create.isPending && <Spinner className="mr-2 h-3.5 w-3.5" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
